import { useCallback, useMemo } from 'react';

import { useStoreContext } from 'features/store';

import { IItem, ISanityTextNode } from '@rbi-ctg/menu';
import { ItemAvailabilityStatus } from 'enums/menu';
import { useFeatureMenu } from 'hooks/use-feature-menu';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useMenuContext } from 'state/menu';
import { useMainMenuContext } from 'state/menu/main-menu';
import { useServiceModeContext } from 'state/service-mode';
import { isAvailableForActiveDayParts, itemIsAvailable } from 'utils/availability';

import { getPosVendorFromStore } from './menu/get-pos-vendor-from-store';
import { useEnableUnifiedMenu } from './use-enable-unified-menu';

export interface IAddOnSectionOption {
  _key: string;
  maxAmount: number;
  option: IItem;
}
export interface IAddOnSection {
  _id: string;
  maxAmount: number;
  name: ISanityTextNode;
  options: IAddOnSectionOption[];
}

type UseFeatureMenuAddOnOptions = {
  ignoreEnableAddExtras?: boolean;
};

const useFeatureMenuAddOnAvailability = () => {
  const { serviceMode } = useServiceModeContext();
  const { store, prices } = useStoreContext();
  const { featureMenu, featureMenuLoading } = useFeatureMenu();
  const { activeDayParts } = useMainMenuContext();
  const { getOneMenuItemAvailability } = useMenuContext();
  const isUnifiedMenuEnabled = useEnableUnifiedMenu();
  const enableAddExtras = useFlag(LaunchDarklyFlag.ENABLE_ADD_EXTRAS);

  const isAvailableOption = useCallback(
    (option: IAddOnSectionOption) => {
      if (isUnifiedMenuEnabled) {
        return (
          getOneMenuItemAvailability(option.option._id).availabilityStatus ===
          ItemAvailabilityStatus.AVAILABLE
        );
      }

      const vendor = getPosVendorFromStore(store, serviceMode);
      const isAvailableDayPart = activeDayParts.length
        ? isAvailableForActiveDayParts({ activeDayParts, menuData: option.option })
        : true;
      const isPriceAvailable = itemIsAvailable(option.option, vendor, prices);
      return isPriceAvailable && isAvailableDayPart;
    },
    [isUnifiedMenuEnabled, store, serviceMode, getOneMenuItemAvailability, activeDayParts, prices]
  );

  const filterAvailableOptions = useCallback(
    (options: IAddOnSectionOption[]) => {
      return options.filter(isAvailableOption);
    },
    [isAvailableOption]
  );

  const hasAvailableAddOns = useCallback(() => {
    if (!featureMenu?.cartAddOnSections || !enableAddExtras) {
      return false;
    }

    return (featureMenu.cartAddOnSections as IAddOnSection[]).some(section =>
      (section?.options ?? []).some(isAvailableOption)
    );
  }, [featureMenu, enableAddExtras, isAvailableOption]);

  return {
    enableAddExtras,
    featureMenu,
    featureMenuLoading,
    filterAvailableOptions,
    hasAvailableAddOns,
  };
};

export const useHasAvailableFeatureMenuAddOn = () => {
  const { featureMenuLoading, hasAvailableAddOns } = useFeatureMenuAddOnAvailability();

  return {
    hasAvailableAddOns,
    loading: featureMenuLoading,
  };
};

const useFeatureMenuAddOn = (options?: UseFeatureMenuAddOnOptions) => {
  const {
    featureMenu,
    featureMenuLoading,
    enableAddExtras,
    filterAvailableOptions,
  } = useFeatureMenuAddOnAvailability();
  const shouldIgnoreEnableAddExtras = options?.ignoreEnableAddExtras ?? false;

  const sections: IAddOnSection[] = useMemo(() => {
    if (!featureMenu?.cartAddOnSections || (!enableAddExtras && !shouldIgnoreEnableAddExtras)) {
      return [];
    }

    return (featureMenu.cartAddOnSections as IAddOnSection[]).reduce(
      (acc: IAddOnSection[], section: IAddOnSection) => {
        const availableOptions = filterAvailableOptions(section?.options ?? []);
        // Returning section only if it has available options
        return availableOptions.length ? [...acc, { ...section, options: availableOptions }] : acc;
      },
      []
    );
  }, [featureMenu, filterAvailableOptions, enableAddExtras, shouldIgnoreEnableAddExtras]);

  return {
    sections,
    loading: featureMenuLoading,
  };
};

export default useFeatureMenuAddOn;
